<template>
  <Layout>
    <template #header-container>
      <PageHeader :title="title" :subTitle="subTitle" isSticky>
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButton
              variant="primary"
              :text="$t('global.addField')"
              icon="icon-fill_plus"
              @click="$router.push({ name: 'Delivery Scheduler Create' })"
            />
          </div>
        </template>
      </PageHeader>
    </template>

    <McTable
      withCard
      searchable
      stickyHeader
      hasEmit:deleteError
      class="mc-delivery-scheduler"
      :apiClient="apiClient"
      :fields="fields"
      :bulkDeleteApi="true"
      :activeFilters="activeFilters"
      @deleteError="handleDeleteError"
    >
      <template #before-table="{ isSelected, showDeleteModal }">
        <div class="d-flex justify-content-between mb-3">
          <McInlineFilter
            :filterItems="inlineFilterItems"
            :activeKeys="activeFilters.status"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'status', values: $event }])"
          />
          <McButton
            v-if="isSelected"
            variant="secondary-red"
            icon="icon-outline_trash"
            :text="$t('global.delete')"
            @click="showDeleteModal"
          />
        </div>
      </template>

      <template #cell(name)="{ value, item }">
        <div class="text-primary primary-field" @click="handleClickRow(item)">
          <div>
            {{ value }}
          </div>

          <i v-if="item.is_error" class="icon-fill_alert-circle" />
        </div>
      </template>

      <template #cell(status.Name)="{ value, item }">
        <template>
          <span
            v-if="item.status?.Color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.Color }"
          ></span>
          <span class="mc-text-status">{{ value }}</span>
        </template>
      </template>

      <template #cell()="{ value, field }">
        <template v-if="['start_date', 'end_date', 'next_run'].includes(field.key)">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">{{ formatIndexDate(value).time }}</div>
        </template>
        <template v-else>{{ value }}</template>
      </template>
    </McTable>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import McInlineFilter from '@/components/McInlineFilter'

import * as DeliveryScheduler from '@/services/rest-api/lib/scheduler/scheduler'

import fields from './lib/fields/index-table-fields.js'
import inlineFilterItems from './lib/constants/inline-filter-items.js'
import filterMappings from './lib/constants/filter-mappings'
import { formatIndexDate } from '@/helpers/tzDate'

export default {
  page: {
    title: 'Delivery Scheduler',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTable,
    McInlineFilter,
  },
  data() {
    return {
      fields,

      activeFilters: {},
      apiErrorMappings: {
        'invalid status': 'Gagal dihapus. Penjadwalan hanya bisa dihapus saat status Tidak Aktif.',
        default: 'Gagal dihapus.',
      },
    }
  },
  computed: {
    title() {
      return 'Penjadwalan Pengiriman'
    },
    subTitle() {
      return 'Menampilkan semua penjadwalan pengiriman dan penugasan dalam bentuk tabel.'
    },
    apiClient() {
      let getScheduler = DeliveryScheduler.get(null)

      if (Object.keys(this.activeFilters).length > 0) {
        let queryParamFilters = []

        // mapping the activeFilters to be sent as query params (in Array)
        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          queryParamFilters.push(filter)

          getScheduler = DeliveryScheduler.get(queryParamFilters)
        })
      }

      return {
        get: getScheduler,
        delete: DeliveryScheduler.deleteMany,
      }
    },
    inlineFilterItems() {
      return inlineFilterItems()
    },
  },
  methods: {
    formatIndexDate,

    handleClickRow(item) {
      this.$router.push({ name: 'Delivery Scheduler Detail', params: { id: item.id } })
    },

    handleDeleteError({ errors }) {
      let errorMessages = Object.keys(errors).map((key) => errors[key][0])

      if (errorMessages.includes('invalid status')) {
        this.$mcToast.error(this.apiErrorMappings['invalid status'])
        return
      }

      this.$mcToast.error(this.apiErrorMappings.default)
    },

    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-delivery-scheduler {
  .primary-field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    i {
      font-size: 16px;

      &:before {
        color: $red;
      }
    }
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }
}
</style>
